import { BehaviorSubject } from 'rxjs';

import HowItWorksDialog from '../components/common/HowItWorksDialog';
import UploadProgress from '../containers/upload/UploadProgress';
import { WorkspaceProductType } from '../models/Workspace';
import parseFiles from '../utils/dicomUtils';
import PaymentUtils from '../utils/payment.utils';

export default class PaymentBloc {
  isValidSession$ = new BehaviorSubject(null);
  error$ = new BehaviorSubject(null);
  packageOption$ = new BehaviorSubject('video');
  shouldOpenDashboard$ = new BehaviorSubject(false);
  selectedRecord$ = new BehaviorSubject(null);
  shouldOpenRequestVideoReport$ = new BehaviorSubject(false);
  isLoading$ = new BehaviorSubject(false);
  getStudyTrial$ = new BehaviorSubject(0);
  couponData$ = new BehaviorSubject({});

  constructor({
    paymentRepository,
    userRepository,
    authRepository,
    dialogRepository,
    patientRepository,
    errorRepository,
    uploadProgressRepository,
    casesRepository,
    freshchatService,
    workspaceRepository,
  }) {
    this.paymentRepository = paymentRepository;
    this.userRepository = userRepository;
    this.paymentRepository.planToDowngrade$.next(null);
    this.dialogRepository = dialogRepository;
    this.patientRepository = patientRepository;
    this.errorRepository = errorRepository;
    this.uploadProgressRepository = uploadProgressRepository;
    this.casesRepository = casesRepository;
    this.freshchatService = freshchatService;
    this.authRepository = authRepository;
    this.workspaceRepository = workspaceRepository;

    this.isMediphanyWorkspaceSubscribe = this.authRepository.isMediphanyWorkspace$.subscribe((value) => {
      this.handleCheckIsMediphanyWorkspace();
    });
  }

  dispose() {
    this.isMediphanyWorkspaceSubscribe.unsubscribe();
  }

  async isValidSession(sessionId) {
    try {
      const result = await this.paymentRepository.isValidSession(sessionId);
      this.isValidSession$.next(result);
    } catch (ignore) {
      this.error$.next('Invalid session id');
    }
  }

  async createPackageCheckoutSession(plan, currentRoute = null) {
    const currentPlan = this.userRepository.userDetails$.getValue().userPlan;
    // if the user is able to purchase this plan (IE: isn't a downgrade)
    if (!currentPlan || PaymentUtils.getShouldDowngrade(currentPlan.studyCount, plan.name)) {
      const { sessionId } = await this.paymentRepository.createPackageCheckoutSession(plan.name, currentRoute);
      const result = await this.redirectToCheckout(sessionId);
      if (result.error) {
        console.log('createPackageCheckoutSession error', result.error);
      }
    } else {
      plan.isPackage = true;
      this.paymentRepository.planToDowngrade$.next(plan);
    }
  }

  async createCaseCheckoutSession(plan) {
    const { availableReport } = this.userRepository.userDetails$?.getValue();
    const userDetails = this.userDetails$.getValue();

    if (!availableReport) {
      const selectedRecord = this.selectedRecord$.getValue();
      const caseId = selectedRecord?.case?.id;

      if (!caseId && userDetails?.workspace?.canRequestBeforePay) {
        this.shouldOpenRequestVideoReport$.next(true);
        return;
      }

      const { sessionId } = await this.paymentRepository.createCaseCheckoutSession(null, '/package-plans', plan.name, caseId);
      const result = await this.redirectToCheckout(sessionId);
      if (result.error) {
        this.errorRepository.error$.next(result.error);
      }
      return;
    }

    const { type } = availableReport;
    const typeText = type === 'Express' ? `an ${type}` : `a ${type}`;

    this.errorRepository.error$.next(`You ${typeText} video report available. Please use it before you could buy another one.`);
  }

  async changeToDemoUserState() {
    const isPatientDemoAccount = this.userRepository.getIsPatientDemoAccount();
    if (isPatientDemoAccount) {
      this.shouldOpenDashboard$.next(true);
      return;
    }

    this.userRepository.setIsPatientDemoAccount(1);
    this.userRepository.removeIsPatientDashboardTourShown();
    this.shouldOpenDashboard$.next(true);
  }

  async createSubscriptionCheckoutSession(plan, currentRoute = null) {
    const currentPlan = this.userRepository.userDetails$.getValue().userPlan.name;
    // if the user is able to purchase this plan (IE: isn't a downgrade)
    if (!currentPlan || PaymentUtils.getAvailablePlans(currentPlan).includes(plan.name)) {
      const { sessionId } = await this.paymentRepository.createSubscriptionCheckoutSession(plan.name, currentRoute);
      const result = await this.redirectToCheckout(sessionId);
      if (result.error) {
        console.log('createSubscriptionCheckoutSession error', result.error);
      }
    } else {
      this.paymentRepository.planToDowngrade$.next(plan);
    }
  }

  get planToDowngrade$() {
    return this.paymentRepository.planToDowngrade$;
  }

  async redirectToCheckout(sessionId) {
    return this.paymentRepository.redirectToCheckout(sessionId);
  }

  openHowItWorksDialog() {
    this.dialogRepository.loadDialog(HowItWorksDialog, {}, true, true);
  }

  openFreshchatDialog() {
    this.freshchatService.openDialog();
  }

  async onUpload(files) {
    try {
      this.uploadProgressRepository.start();
      this.dialogRepository.loadDialog(UploadProgress, { preventClose: true });

      const status = await parseFiles(files);

      const payload = {
        files: status.files,
        title: status.study,
      };
      await this.patientRepository.addStudy(payload, this.uploadProgressRepository.progress$);
      // updating plan details
      await this.userRepository.getCurrentUser();
      this.errorRepository.error$.next({
        message: 'Your files are being processed. Once this is completed you will be notified by email.',
        severity: 'success',
      });
      this.uploadProgressRepository.finish();
      return true;
    } catch (error) {
      this.errorRepository.error$.next({
        message: error.message,
        severity: 'error',
      });
      this.uploadProgressRepository.fail();
      return false;
    }
  }

  handleChangePackageOption(option) {
    this.packageOption$.next(option);
  }

  get userDetails$() {
    return this.userRepository.userDetails$;
  }

  async getCurrentStudy(studyId) {
    if (!studyId) return;

    try {
      this.isLoading$.next(true);
      const currentStudy = await this.patientRepository.getRecord(studyId);
      if (currentStudy.case?.status !== 'not-paid') {
        this.isLoading$.next(false);
        return;
      }

      this.selectedRecord$.next(currentStudy);
      this.getStudyTrial$.next(0);

      this.isLoading$.next(false);
    } catch (error) {
      this.isLoading$.next(false);

      const studyTrial = this.getStudyTrial$.getValue();
      if (studyTrial === 0) {
        this.getNotPaidCase();
        this.getStudyTrial$.next(studyTrial + 1);
      }
    }
  }

  async getNotPaidCase() {
    try {
      this.isLoading$.next(true);
      await this.casesRepository.getNotPaidCases();
      const notPaidCases = await this.notPaidCases$.getValue();

      if (!notPaidCases?.length) {
        this.isLoading$.next(false);
        return;
      }

      const notPaidCase = notPaidCases?.[0] || undefined;
      const resourceId = notPaidCase?.resource?.id;

      this.isLoading$.next(false);
      await this.getCurrentStudy(resourceId);
    } catch (e) {
      this.isLoading$.next(false);
    }
  }

  async handleGetWorkspaceDiscountDetail() {
    const result = await this.workspaceRepository.discountDetail();
    this.couponData$.next(result?.discount);
  }

  handleCheckIsMediphanyWorkspace() {
    const isMediphanyWorkspace = this.authRepository.isMediphanyWorkspace$.getValue();
    const workspaceDetails = this.authRepository.userWorkspace$.getValue();

    if (!isMediphanyWorkspace && workspaceDetails.productType !== WorkspaceProductType.whiteGlove.value) this.shouldOpenDashboard$.next(true);
  }

  get notPaidCases$() {
    return this.casesRepository.notPaidCases$;
  }

  get notPaidCasesTotal$() {
    return this.casesRepository.notPaidCasesTotal$;
  }
}
